import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import {
  hasZeroModuleAccess,
  isModuleTabVisible,
  shouldApplyRouteModuleFilter,
} from '../constants/moduleAccess.ts';
import { useOrgModuleAccess } from '../hooks/useOrgModuleAccess.ts';
import { resolveRouteModuleRequirement } from '../routing/tenantModuleRoutes.ts';

function getStoredOrganizationId(): string {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem('organization_id') ?? '';
}

type ModuleAccessGateProps = {
  children: React.ReactNode;
};

function NoModuleAccessMessage({
  title,
  message,
  showGoBack,
}: {
  title: string;
  message: string;
  showGoBack: boolean;
}) {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-6 py-12">
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl border border-red-200/80 bg-white shadow-xl shadow-red-100/60"
        role="alert"
        aria-live="polite"
      >
        <div className="px-8 pb-8 pt-10 text-center">
          <div className="relative mx-auto mb-6 flex h-[4.5rem] w-[4.5rem] items-center justify-center">
            <span className="absolute inset-0 rounded-2xl bg-red-100/70" aria-hidden="true" />
            <span
              className="absolute -inset-1 rounded-2xl bg-red-50 ring-1 ring-red-200/60"
              aria-hidden="true"
            />
            <Lock className="relative h-9 w-9 text-red-600" strokeWidth={1.75} aria-hidden="true" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-red-600">Access restricted</p>
          <h1 className="mt-2 text-2xl font-bold tracking-tight text-gray-900">{title}</h1>
          <p className="mt-3 text-sm leading-relaxed text-gray-600">{message}</p>
          {showGoBack && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="mt-8 inline-flex items-center justify-center rounded-lg border border-red-200 bg-white px-4 py-2.5 text-sm font-medium text-red-700 shadow-sm transition-colors hover:border-red-300 hover:bg-red-50"
            >
              Go back
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const ModuleAccessGate: React.FC<ModuleAccessGateProps> = ({ children }) => {
  const location = useLocation();
  const organizationId = getStoredOrganizationId();
  const { data: moduleAccess, isSuccess } = useOrgModuleAccess();
  const requirement = resolveRouteModuleRequirement(location.pathname);

  if (!organizationId) {
    return <>{children}</>;
  }

  if (!isSuccess) {
    return (
      <div className="flex min-h-[12rem] items-center justify-center px-8 py-12 text-sm text-gray-500">
        Loading…
      </div>
    );
  }

  // Org∩role empty: block module-gated pages with contact-admin (ungated routes e.g. org-admin still ok).
  if (hasZeroModuleAccess(moduleAccess)) {
    if (!requirement) {
      return <>{children}</>;
    }
    return (
      <NoModuleAccessMessage
        title="No module access"
        message="You do not have access to any modules in this organization. Contact your organization administrator."
        showGoBack={false}
      />
    );
  }

  if (!requirement) {
    return <>{children}</>;
  }

  if (!shouldApplyRouteModuleFilter(moduleAccess)) {
    return <>{children}</>;
  }

  if (isModuleTabVisible(requirement, moduleAccess)) {
    return <>{children}</>;
  }

  return (
    <NoModuleAccessMessage
      title="No module access"
      message="Your role does not include permission to open this area. If you need access, contact your organization administrator."
      showGoBack
    />
  );
};

export default ModuleAccessGate;
